import { Box, Typography } from "@mui/material";
import {
  BADGE_CONFIG,
  BADGE_SORT_ORDER,
  DEFAULT_BADGE_COLOR,
} from "common/badgeConfig";

interface BadgesCardProps {
  badges: Record<string, number>;
}

export default ({ badges }: BadgesCardProps) => {
  if (!badges || Object.keys(badges).length === 0) {
    return null;
  }

  // Get all badges the user has
  let userBadges = Object.entries(badges)
    .filter(([_, count]) => count > 0)
    .map(([badgeName, count]) => ({ name: badgeName, count }));

  if (userBadges.length === 0) {
    return null;
  }

  // Sort badges using predefined order
  userBadges.sort((a, b) => {
    const aIndex = BADGE_SORT_ORDER.indexOf(a.name);
    const bIndex = BADGE_SORT_ORDER.indexOf(b.name);
    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  // Count total unique badges
  const totalBadges = userBadges.length;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Typography variant="h3">Badges </Typography>
        <Typography variant="bodyHeavy" color="text.secondary">
          {totalBadges}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: "16px",
        }}
      >
        {userBadges.map(({ name, count }) => {
          const config = BADGE_CONFIG[name];
          if (!config) return null;

          const BadgeIcon = config.icon;
          const badgeColor =
            config.color === DEFAULT_BADGE_COLOR ? undefined : config.color;

          return (
            <Box
              key={name}
              sx={{
                display: "flex",
                gap: "16px",
                alignItems: config.isSingular ? "center" : "flex-start",
              }}
            >
              <Box
                sx={(theme) => ({
                  color: badgeColor || theme.palette.text.primary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.border}`,
                })}
              >
                <BadgeIcon size={32} strokeWidth={2} />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: config.isSingular ? 0 : "4px",
                  flex: 1,
                }}
              >
                <Typography variant="h5">{config.label}</Typography>
                <Typography variant="bodySmall" color="text.secondary">
                  {config.description}
                </Typography>
                {!config.isSingular && (
                  <Typography variant="bodySmallHeavy">
                    Earned {count} {count === 1 ? "time" : "times"}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
